import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

interface VersionInfo {
  version: string;
  builtAt: string;
  artifact: string;
}

const POLL_INTERVAL_MS = 60_000;
const FIRST_POLL_DELAY_MS = 5_000;

/**
 * Story 107.3 — detects "this tab is running an older bundle than
 * what's currently deployed" by polling /api/v1/version every minute
 * and comparing against the build-time-injected __APP_VERSION__.
 *
 * Safe to call before login: the endpoint is permitAll. The first
 * poll is delayed 5s to keep boot fast; subsequent polls every
 * minute.
 *
 * Decision: NO auto-reload. The toast surfaces an explicit "Refrescar"
 * action so a half-filled form doesn't get nuked. If at some point we
 * need a forced upgrade (security CVE in old client), the version
 * endpoint can return a `minClientVersion` field and we add a hard-
 * reload branch below — for now the soft prompt is enough.
 */
export function useUpdateAvailable(): {
  available: boolean;
  loadedVersion: string;
  serverVersion: string | null;
  refresh: () => void;
} {
  const [available, setAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = () => {
      apiClient
        .get<VersionInfo>('/version')
        .then(({ data }) => {
          if (cancelled) return;
          setServerVersion(data.version);
          // 'dev' on either side disables the comparison — local dev
          // shouldn't nag, and a server reporting 'dev' usually means
          // a misconfigured deploy that we'd rather flag separately.
          if (
            data.version &&
            data.version !== 'dev' &&
            __APP_VERSION__ !== 'dev' &&
            data.version !== __APP_VERSION__
          ) {
            setAvailable(true);
          }
        })
        .catch(() => {
          // Network blip — don't change state. Next interval retries.
        });
    };

    const first = window.setTimeout(check, FIRST_POLL_DELAY_MS);
    const interval = window.setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(interval);
    };
  }, []);

  return {
    available,
    loadedVersion: __APP_VERSION__,
    serverVersion,
    refresh: () => window.location.reload(),
  };
}
